import { CircleAlert } from 'lucide-react';
import { createContext, type ReactNode, useContext, useId } from 'react';
import { cn } from '../lib/cn';

interface FieldContextValue {
  id: string;
  describedBy?: string;
  invalid: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/** Pola formularza (Input, Textarea, Select…) czytają z kontekstu id, aria-describedby i aria-invalid. */
export function useFieldControl(props: { id?: string; 'aria-describedby'?: string; 'aria-invalid'?: unknown }) {
  const ctx = useContext(FieldContext);
  const describedBy = [props['aria-describedby'], ctx?.describedBy].filter(Boolean).join(' ') || undefined;
  const invalid = props['aria-invalid'] === true || props['aria-invalid'] === 'true' || Boolean(ctx?.invalid);
  return { id: props.id ?? ctx?.id, 'aria-describedby': describedBy, 'aria-invalid': invalid || undefined };
}

export function Label({ htmlFor, children, className }: { htmlFor?: string; children: ReactNode; className?: string }) {
  return (
    <label htmlFor={htmlFor} className={cn('text-caption font-medium text-fg-secondary', className)}>
      {children}
    </label>
  );
}

/**
 * Pole z etykietą, podpowiedzią i błędem. Błąd zastępuje podpowiedź i jest ogłaszany czytnikom ekranu.
 */
export function Field({
  label,
  hint,
  error,
  id: idProp,
  labelAction,
  children,
  className,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  id?: string;
  /** Element po prawej stronie etykiety (np. link „Nie pamiętasz hasła?”). */
  labelAction?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const autoId = useId();
  const id = idProp ?? `f${autoId}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: Boolean(error) }}>
      <div className={cn('flex flex-col gap-1.5', className)}>
        {(label || labelAction) && (
          <div className="flex items-baseline justify-between gap-3">
            {label && <Label htmlFor={id}>{label}</Label>}
            {labelAction}
          </div>
        )}
        {children}
        {error ? (
          <p id={errorId} role="alert" className="flex items-start gap-1.5 text-caption text-danger">
            <CircleAlert size={14} strokeWidth={1.75} aria-hidden className="mt-px shrink-0" />
            <span>{error}</span>
          </p>
        ) : (
          hint && (
            <div id={hintId} className="text-caption text-fg-muted">
              {hint}
            </div>
          )
        )}
      </div>
    </FieldContext.Provider>
  );
}
